import dayjs from "dayjs";
import "dayjs/locale/fr";
import { groupMonths } from "./regroupment";
import { addRacineToCatgories } from "./tableUtils";
import { groupElementsByDate } from "./groupElementsByDate";
import { parseMonthYear } from "./filters";

export const ROW_HEIGHT = 38;
export const HEADER_HEIGHT = 120;
export const LISTING_HEIGHT = 54;

const generateEmptyRow = (key, renderCell) => ({
  key,
  name: "",
  isEmpty: true,
});

function findMaxDepth(obj) {
  if (typeof obj !== "object" || obj === null) {
    return 0;
  }

  let maxDepth = 0;

  for (const key in obj) {
    if (maxDepth < obj[key].length) {
      maxDepth = obj[key].length;
    }
  }

  return maxDepth;
}

function addIdKeyToCategories(categories) {
  return categories.map((category) => {
    const newCategory = { ...category, key: category.id };
    if (category.children) {
      newCategory.children = addIdKeyToCategories(category.children);
    }
    return newCategory;
  });
}

const calculateMonthTotals = (categories, rangesArray) => {
  const processMonthRange = (category, monthsRange, months) => {
    const rangeKey = monthsRange.join(", ");

    monthsRange.forEach((month) => {
      if (!category?.elements?.[month]) return;

      if (!months[month]) months[month] = 0;

      category.elements[month].forEach((item) => {
        months[month] += item.amount;

        if (month !== rangeKey) {
          if (!months[rangeKey]) months[rangeKey] = 0;
          months[rangeKey] += item.amount;
        }
      });
    });
  };

  const processCategory = (category, months = {}) => {
    rangesArray.forEach((monthsRange) => {
      processMonthRange(category, monthsRange, months);
    });

    if (category.children?.length) {
      category.children.forEach((subCategory) => {
        processCategory(subCategory, months);
      });
      category.children = calculateMonthTotals(category.children, rangesArray);
    }

    return { ...category, ...months };
  };

  return categories.map((category) => processCategory(category));
};

function truncateWithThreeDots(inputString, maxLength) {
  return inputString?.length > maxLength
    ? inputString.slice(0, maxLength) + "..."
    : inputString;
}

const generateClassName = (status) => {
  switch (status) {
    case "pointé":
      return "pointed-class";
    case "engagé":
      return "engaged-class";
    case "prévisionnel":
      return "prev-class";
    case "simulation":
      return "simul-class";
    default:
      return "default-class";
  }
};

const generateClassNameCompta = (status, type, isSpecial, isListing) => {
  if (type) {
    return "compta-sold-class";
  }
  if (isSpecial && isListing) {
    return "cell-with-solded-compta-element";
  }
  switch (status) {
    case "réel":
      return "real-class";
    case "simulation":
      return "simul-class";
  }
};

function calculateBalanceSums(array) {
  return array?.reduce(
    (acc, curr) => {
      const synthese = parseFloat(curr.value);

      if (synthese > 0) {
        acc.positiveBalance += synthese;
      } else {
        acc.negativeBalance -= synthese;
      }

      acc.finalBalance = acc.positiveBalance - acc.negativeBalance;

      return acc;
    },
    {
      positiveBalance: 0,
      negativeBalance: 0,
      finalBalance: 0,
    }
  );
}

const findById = (id, items) => {
  for (const item of items) {
    if (item.id == id) {
      return item;
    }
    if (item.children && item.children.length > 0) {
      const found = findById(id, item.children);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

const findIndexById = (id, items) => {
  for (let i = 0; i < items.length; i++) {
    if (items[i].id === id) {
      return i;
    }
  }
  return -1;
};

const findNextItemSameLevel = (id, items) => {
  const parent = items.find(
    (item) => item.children && findIndexById(id, item.children) !== -1
  );
  if (parent) {
    const index = findIndexById(id, parent.children);
    if (index !== -1 && index + 1 < parent.children.length) {
      return parent.children[index + 1];
    }
  }
  return null;
};

const updateSynthesis = (arr1, arr2) => {
  if (!arr2) return [];

  const processArray = (array) => {
    return array.map((item) => {
      const matchingSynthesis = arr2.find((sItem) => {
        return item.isRacine
          ? sItem.id == +item.categoryId
          : sItem.id == +item.id;
      });

      const updatedItem = {
        ...item,
        synthese: matchingSynthesis?.value ?? null,
      };

      if (item.children && item.children.length > 0) {
        updatedItem.children = processArray(item.children);

        const allChildrenHaveNoValues = updatedItem.children
          .filter((child) => !child?.isRacine)
          .every((child) => child.synthese !== 0 && child.synthese == null);

        const allChildrenHaveNoNames = updatedItem.children.every(
          (child) => !child.name
        );

        if (
          matchingSynthesis &&
          !allChildrenHaveNoNames &&
          !item.children[0].isRacine
        ) {
          const racineCategory = {
            id: `racine-${item.id}`,
            name: `racine de ${item.name}`,
            isRacine: true,
            plancomptableId: item.id,
            objectif: null,
            levelId: updatedItem.levelId + 1,
            isSpecial: item?.isSpecial,
            children: [],
            synthese: updatedItem.synthese || 0,
          };

          updatedItem.children.unshift(racineCategory);
        }

        // Sum up children's synthese values
        const childrenSum = updatedItem.children.reduce((sum, child) => {
          if (!child.isRacine && child.synthese != null) {
            return sum + child.synthese;
          }
          return sum;
        }, 0);

        if (!allChildrenHaveNoValues) {
          updatedItem.synthese = (updatedItem.synthese || 0) + childrenSum;
        }
      }

      return updatedItem;
    });
  };
  return processArray(arr1);
};

const updateSynthesisPlancompta = (arr1, arr2) => {
  if (!arr2) return [];

  const processArray = (array) => {
    return array.map((item) => {
      const matchingSynthesis = arr2.find((sItem) => {
        return item.isRacine
          ? sItem.id == +item.categoryId
          : sItem.id == +item.id;
      });

      const updatedItem = {
        ...item,
        synthese: matchingSynthesis?.value ?? null,
      };

      if (item.children && item.children.length > 0) {
        updatedItem.children = processArray(item.children);

        const allChildrenHaveNoValues = updatedItem.children
          .filter((child) => !child?.isRacine)
          .every((child) => child.synthese !== 0 && child.synthese == null);

        const allChildrenHaveNoNames = updatedItem.children.every(
          (child) => !child.name
        );

        if (
          matchingSynthesis &&
          !allChildrenHaveNoNames &&
          !item.children[0].isRacine
        ) {
          const racineCategory = {
            id: `racine-${item.id}`,
            name: `racine de ${item.name}`,
            isRacine: true,
            plancomptableId: item.id,
            objectif: null,
            levelId: updatedItem.levelId + 1,
            isSpecial: item?.isSpecial,
            children: [],
            synthese: updatedItem.synthese || 0,
          };

          updatedItem.children.unshift(racineCategory);
        }

        // Sum up children's synthese values
        const childrenSum = updatedItem.children.reduce((sum, child) => {
          if (!child.isRacine && child.synthese != null) {
            return sum + child.synthese;
          }
          return sum;
        }, 0);

        if (!allChildrenHaveNoValues) {
          updatedItem.synthese = (updatedItem.synthese || 0) + childrenSum;
        }
      }

      return updatedItem;
    });
  };
  return processArray(arr1);
};

function getMaxDepth(groupedElements, category) {
  let maxDepth = 0;
  groupedElements.forEach((range) => {
    let currentDepth = 0;
    range.forEach((month) => {
      const monthData = category?.[month];
      if (monthData) {
        currentDepth = currentDepth + monthData.length;
      }
    });

    if (currentDepth > maxDepth) {
      maxDepth = currentDepth;
    }
  });

  return maxDepth;
}

function getElementsPerRange(groupedElements, categories) {
  const elementsPerRange = {};

  groupedElements.forEach((range) => {
    const elements = [];
    range.forEach((month) => {
      const monthData = categories[month];
      if (monthData) {
        elements.push(...monthData);
      }
    });

    if (elements.length > 0) {
      const rangeKey = range.join(", ");
      elementsPerRange[rangeKey] = elements;
    }
  });

  return elementsPerRange;
}

function sortByAmount(objToSort, sortType) {
  const sortedObj = {};

  for (const key in objToSort) {
    if (Object.hasOwn(objToSort, key)) {
      const innerArray = objToSort[key];

      const sortedArray = innerArray.slice().sort((a, b) => {
        if (sortType === "asc") {
          if (a.amount === b.amount) {
            return new Date(a.createdAt) - new Date(b.createdAt);
          } else {
            return a.amount - b.amount;
          }
        } else if (sortType === "desc") {
          if (a.amount === b.amount) {
            return new Date(b.createdAt) - new Date(a.createdAt);
          } else {
            return b.amount - a.amount;
          }
        } else if (sortType === "reset") {
          return new Date(a.createdAt) - new Date(b.createdAt);
        } else if (sortType === "TresoDateAsc") {
          if (a.dateTreso === b.dateTreso) {
            return new Date(b.createdAt) - new Date(a.createdAt);
          } else {
            return new Date(a.dateTreso) - new Date(b.dateTreso);
          }
        } else if (sortType === "TresoDateDesc") {
          if (a.dateTreso === b.dateTreso) {
            return new Date(a.createdAt) - new Date(b.createdAt);
          } else {
            return new Date(b.dateTreso) - new Date(a.dateTreso);
          }
        } else if (sortType === "ComptaDateAsc") {
          const aDate = a.dateCompta || a.dateTreso;
          const bDate = b.dateCompta || b.dateTreso;

          if (aDate === bDate) {
            return new Date(a.createdAt) - new Date(b.createdAt);
          }

          return new Date(aDate) - new Date(bDate);
        } else if (sortType === "ComptaDateDesc") {
          const aDate = a.dateCompta || a.dateTreso;
          const bDate = b.dateCompta || b.dateTreso;

          if (aDate === bDate) {
            return new Date(b.createdAt) - new Date(a.createdAt);
          }

          return new Date(bDate) - new Date(aDate);
        }
      });

      sortedObj[key] = sortedArray;
    }
  }

  return sortedObj;
}

function getElementsPerRangeElements(ranges, dataObject, sortValue) {
  const result = {};
  ranges.forEach((range) => {
    const elements = [];
    range.forEach((month) => {
      const monthData = dataObject[month];
      if (monthData) {
        elements.push(...monthData);
      }
    });

    if (elements.length > 0) {
      const rangeKey = range.join(", ");
      result[rangeKey] = elements;
    }
  });

  return sortByAmount(result, sortValue);
}

function createArrayOfObjects(elementsPerRange) {
  const ranges = Object.values(elementsPerRange);
  const maxLength = Math.max(...ranges.map((arr) => arr.length));

  const resultArray = [];

  for (let i = 0; i < maxLength; i++) {
    const objectWithElements = {};

    Object.entries(elementsPerRange).forEach(([range, elements]) => {
      if (elements[i] !== undefined) {
        objectWithElements[range] = elements[i];
      }
    });

    resultArray.push(objectWithElements);
  }

  return resultArray;
}

const calculateFooterSum = (currentElement, mode) => {
  const initialValue = {
    positiveBalance: 0,
    negativeBalance: 0,
    finalBalance: 0,
  };

  const totals = currentElement.reduce((accumulator, element) => {
    accumulator.positiveBalance += parseFloat(element.positiveBalance);
    accumulator.negativeBalance += parseFloat(element.negativeBalance);

    if (mode === "compta") {
      accumulator.finalBalance += parseFloat(element.finalBalance);
    } else {
      accumulator.finalBalance = parseFloat(element.finalBalance);
    }

    return accumulator;
  }, initialValue);

  return totals;
};

function findIdsAtLevels(data, targetId) {
  const result = [];

  function helper(nodes, level, found) {
    if (!result[level]) {
      result[level] = [];
    }

    nodes.forEach((node) => {
      result[level].push(node.id);
      if (node.id === targetId) {
        found = true;
      }
      if (node.children && node.children.length > 0) {
        helper(node.children, level + 1, found);
      }
    });

    return found;
  }

  helper(data, 0, false);

  return result;
}

const getBorderTopStyle = (item) => {
  if (item?.levelId === 1) return "0.5px solid rgb(187 187 187)";
  if (item?.name) return "0.5px solid rgb(235, 235, 235)";
  return "0";
};

function processCategory(category, monthsGrouped) {
  const currentCategory = { ...category };
  if (currentCategory.elements) {
    const elementsPerRange = getElementsPerRange(
      monthsGrouped,
      currentCategory.elements
    );

    const finalElements = createArrayOfObjects(elementsPerRange);

    if (currentCategory.children) {
      const childrenArray = [...currentCategory.children];
      const reversedArrayElements = finalElements.toReversed();

      for (let i = 0; i < reversedArrayElements.length; i++) {
        const { children, ...category } = currentCategory;
        childrenArray.unshift({
          ...reversedArrayElements[i],
          id: `${currentCategory.id}-${i}`,
          isForelements: true,
          category,
          isMasked: category.isMasked,
          isImported: true,
        });
      }
      currentCategory.children = childrenArray.map((childCategory) =>
        processCategory(childCategory)
      );
    }
  }
  return currentCategory;
}

const mergeObjectsByKey = (obj1, obj2) => {
  const result = { ...obj1 };
  for (const key in obj2) {
    if (obj1.hasOwnProperty(key)) {
      if (
        typeof obj1[key] === "object" &&
        typeof obj2[key] === "object" &&
        !Array.isArray(obj1[key]) &&
        !Array.isArray(obj2[key])
      ) {
        result[key] = mergeObjectsByKey(obj1[key], obj2[key]);
      } else if (Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
        result[key] = obj1[key].concat(obj2[key]);
      } else {
        result[key] = obj2[key];
      }
    } else {
      result[key] = obj2[key];
    }
  }
  return result;
};

const createImportedElementsDataSourceForListing = (
  importedElements,
  RegroupmentValue,
  selectedMonths
) => {
  const elementsByDate = {};

  importedElements?.forEach((element) => {
    const dateMode = element?.dateTreso;
    const date = parseMonthYear(dateMode);
    const key = date.format("MMMM YYYY");

    if (!elementsByDate[key]) {
      elementsByDate[key] = [];
    }
    elementsByDate[key].push(element);
  });

  return elementsByDate;
};

const calculateImportedElementsSyntheseSum = (
  importedElements,
  selectedMonthsSynthesis
) => {
  if (
    !importedElements?.length ||
    !selectedMonthsSynthesis?.length
  ) {
    return null;
  }

  const synthesisMonthsSet = new Set(
    selectedMonthsSynthesis.map((m) => m.toLowerCase())
  );

  let hasElementsInPeriod = false;
  const sum = importedElements.reduce((acc, element) => {
    const dateTreso = element?.dateTreso;
    if (!dateTreso) return acc;

    const elementMonth = dayjs(dateTreso).locale("fr").format("MMMM YYYY").toLowerCase();
    if (!synthesisMonthsSet.has(elementMonth)) return acc;

    hasElementsInPeriod = true;
    const amount = parseFloat(element?.amount) || 0;
    return acc + amount;
  }, 0);

  return hasElementsInPeriod ? sum : null;
};

const createImportedElementsDataSource = (
  importedElements,
  RegroupmentValue,
  selectedMonths,
  { synthesis = false, selectedMonthsSynthesis = [] } = {}
) => {
  const monthsGrouped = groupMonths(RegroupmentValue, selectedMonths);
  const syntheseSum =
    synthesis && selectedMonthsSynthesis?.length > 0
      ? calculateImportedElementsSyntheseSum(
          importedElements,
          selectedMonthsSynthesis
        )
      : null;

  const importedElementsObject = [
    {
      id: "imported-elements",
      key: "imported-elements",
      index: 0,
      name: "Ecritures importées",
      isImported: true,
      levelId: 1,
      parentId: null,
      children: [],
      isInput: false,
      createdAt: null,
      isMasked: 0,
      isSpecial: 0,
      type: "treso",
      project: null,
      projectId: 223,
      synthese: syntheseSum,
      elements: importedElements.map((element) => ({
        ...element,
        dateTreso: new Date(element.dateTreso).toISOString(),
      })),
    },
  ];

  const processedImportedElements = groupElementsByDate(
    importedElementsObject,
    "treso"
  );

  const racineCategory = addRacineToCatgories(processedImportedElements);

  const importedCategoriesWithSum = calculateMonthTotals(
    racineCategory,
    monthsGrouped
  );

  const processedCategories = importedCategoriesWithSum.map((category) =>
    processCategory(category, monthsGrouped)
  );

  return processedCategories || [];
};

export {
  createImportedElementsDataSourceForListing,
  addIdKeyToCategories,
  calculateBalanceSums,
  calculateFooterSum,
  calculateMonthTotals,
  createArrayOfObjects,
  findById,
  findIdsAtLevels,
  findMaxDepth,
  findNextItemSameLevel,
  generateClassName,
  generateClassNameCompta,
  generateEmptyRow,
  getBorderTopStyle,
  getElementsPerRange,
  getElementsPerRangeElements,
  getMaxDepth,
  truncateWithThreeDots,
  updateSynthesis,
  updateSynthesisPlancompta,
  createImportedElementsDataSource,
  mergeObjectsByKey,
};
